import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import DopaVoiceAgent from './DopaVoiceAgent'
import { useConversation } from '@elevenlabs/react'
import MindPolishLogo from './MindPolishLogo'
import DopaLogo from './DopaLogo'

interface Message {
  type: 'bot' | 'user'
  text: string
  isButton?: boolean
}

interface SupportState {
  currentStep: 'initial' | 'recognized' | 'action' | 'followup' | 'voice'
  recognizedEmotion: string | null
  usedActions: string[]
  emotionalResponses: string[]
  moodContext?: any
}

const SupportBot = () => {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [supportState, setSupportState] = useState<SupportState>({
    currentStep: 'initial',
    recognizedEmotion: null,
    usedActions: [],
    emotionalResponses: []
  })
  const [showBreathing, setShowBreathing] = useState(false)
  const [showJournal, setShowJournal] = useState(false)
  const [showQuiet, setShowQuiet] = useState(false)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [userResponses, setUserResponses] = useState<string[]>([])
  
  // Get the conversation API to be able to end the session when exiting
  const { endSession, status } = useConversation()

  // Load mood context on component mount
  useEffect(() => {
    const moodContext = localStorage.getItem('current_mood_context')
    if (moodContext) {
      try {
        const parsedMood = JSON.parse(moodContext)
        setSupportState(prev => ({ ...prev, moodContext: parsedMood }))
        
        // Start with mood-aware greeting
        const moodAwareGreeting = getMoodAwareGreeting(parsedMood.mood)
        setMessages([{ type: 'bot', text: moodAwareGreeting }])
        
        // Skip to voice agent immediately
        setSupportState(prev => ({
          ...prev,
          currentStep: 'voice',
          recognizedEmotion: parsedMood.mood.toLowerCase(),
          emotionalResponses: [parsedMood.mood]
        }))
        
        // Clear the mood context after using it
        localStorage.removeItem('current_mood_context')
      } catch (error) {
        console.error('Error parsing mood context:', error)
        setMessages([{ type: 'bot', text: 'Hi there. I\'m here with you.' }])
        
        // Skip to voice agent immediately
        setSupportState(prev => ({
          ...prev,
          currentStep: 'voice',
          emotionalResponses: ['uncertain']
        }))
      }
    } else {
      setMessages([{ type: 'bot', text: 'Hi there. I\'m here with you.' }])
      
      // Skip to voice agent immediately
      setSupportState(prev => ({
        ...prev,
        currentStep: 'voice',
        emotionalResponses: ['uncertain']
      }))
    }
  }, [])

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // End session if active when component unmounts
      if (status === 'connected') {
        endSession().catch(err => {
          console.error("Error ending session on unmount:", err)
        })
      }
    }
  }, [status, endSession])

  // Handle exit button click
  const handleExit = useCallback(() => {
    // End the voice session if it's active
    if (status === 'connected') {
      endSession().catch(err => {
        console.error("Error ending session on exit:", err)
      })
    }
    
    // Navigate back to the support options page
    navigate('/support', { replace: true })
  }, [status, endSession, navigate])

  const getMoodAwareGreeting = (mood: string): string => {
    switch (mood.toLowerCase()) {
      case 'good':
        return "I'm glad you're feeling good today. Sometimes it's nice to have someone to share good moments with. What's been contributing to these positive feelings?"
      case 'calm':
        return "It's wonderful that you're feeling calm. That sense of peace is precious. Would you like to talk about what's helping you feel this way?"
      case 'okay':
        return "I hear that you're feeling okay. That's completely valid - you don't have to feel amazing all the time. Sometimes 'okay' is exactly where we need to be. What's on your mind?"
      case 'low':
        return "I'm here with you in this low moment. Feeling down can be really hard, and I want you to know you're not alone. Would you like to share what's been weighing on you?"
      case 'anxious':
        return "I can sense you're feeling anxious right now. Those feelings can be overwhelming, but you're safe here with me. Take your time - what's been making you feel this way?"
      case 'tired':
        return "Being tired - whether it's physical, emotional, or both - can feel so heavy. You're allowed to feel this way, and it's okay to need rest. What's been draining your energy?"
      case 'unsure':
        return "Not knowing exactly how you feel is completely normal. Sometimes our emotions are complex or shifting. I'm here to help you sort through whatever you're experiencing. What's been on your mind lately?"
      case 'frustrated':
        return "I can feel that frustration with you. Those feelings are real and important - you don't have to carry them alone. What's been building up that frustration for you?"
      default:
        return `I see you're feeling ${mood}. Thank you for sharing that with me. Your feelings are valid and important. I'm here to listen and support you. What would you like to talk about?`
    }
  }

  const emotionResponses = {
    overwhelmed: "I hear you. When everything feels like too much, let's take it one step at a time.",
    panicking: "You're safe right now. Let's slow things down together. I'm here with you.",
    'shutting down': "That's okay. Sometimes we need to pause and that's perfectly valid. Take your time.",
    anxious: "Those feelings are real. You're not alone in this. Let's find what helps.",
    stressed: "That sounds really hard. Let's find a gentle way to ease that pressure.",
    tired: "Being tired is hard. Let's find gentle ways to support you right now.",
    sad: "Those feelings are valid and you don't have to carry them alone. I'm here.",
    angry: "Those feelings are real and important. Let's find gentle ways to work with them.",
    confused: "It's okay not to have all the answers. Let's take this slowly together.",
    default: "Thank you for sharing that with me. Your feelings are valid and important."
  }

  // 8 gentle check-in questions
  const checkInQuestions = [
    "What's the strongest emotion you're feeling right now?",
    "How would you describe your energy level today?",
    "What's been weighing on your mind lately?",
    "How has your sleep been affecting you?",
    "What's your relationship with stress like today?",
    "How connected do you feel to the people around you?",
    "What does your body need right now?",
    "If you could change one thing about how you're feeling, what would it be?"
  ]

  const recognizeEmotion = (text: string): string => {
    const lowerText = text.toLowerCase()
    if (lowerText.includes('overwhelm') || lowerText.includes('too much')) return 'overwhelmed'
    if (lowerText.includes('panic') || lowerText.includes('attack')) return 'panicking'
    if (lowerText.includes('shut') || lowerText.includes('shutdown') || lowerText.includes('shutting down')) return 'shutting down'
    if (lowerText.includes('anxious') || lowerText.includes('anxiety')) return 'anxious'
    if (lowerText.includes('stress') || lowerText.includes('stressed')) return 'stressed'
    if (lowerText.includes('tired') || lowerText.includes('exhausted')) return 'tired'
    if (lowerText.includes('sad') || lowerText.includes('down') || lowerText.includes('depressed')) return 'sad'
    if (lowerText.includes('angry') || lowerText.includes('mad') || lowerText.includes('frustrated')) return 'angry'
    if (lowerText.includes('confused') || lowerText.includes('lost')) return 'confused'
    return 'default'
  }

  const handleEmotionButton = (emotion: string) => {
    setMessages(prev => [...prev, { type: 'user', text: emotion, isButton: true }])
    
    const emotionKey = emotion.toLowerCase().replace(' ', '')
    const response = emotionResponses[emotionKey as keyof typeof emotionResponses] || emotionResponses.default
    
    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'bot', text: response }])
      
      // Skip questions and go straight to voice agent
      setSupportState(prev => ({
        ...prev,
        currentStep: 'voice',
        recognizedEmotion: emotionKey,
        emotionalResponses: [emotion]
      }))
    }, 1000)
  }

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      setMessages(prev => [...prev, { type: 'user', text: inputMessage }])
      
      if (supportState.currentStep === 'initial') {
        const emotion = recognizeEmotion(inputMessage)
        const response = emotionResponses[emotion as keyof typeof emotionResponses] || emotionResponses.default
        
        setInputMessage('')
        
        setTimeout(() => {
          setMessages(prev => [...prev, { type: 'bot', text: response }])
          
          // Skip questions and go straight to voice agent
          setSupportState(prev => ({
            ...prev,
            currentStep: 'voice',
            recognizedEmotion: emotion,
            emotionalResponses: [inputMessage]
          }))
        }, 1000)
      } else if (supportState.currentStep === 'recognized') {
        // Handle responses to check-in questions
        const newResponses = [...userResponses, inputMessage]
        setUserResponses(newResponses)
        setInputMessage('')
        
        // Skip remaining questions and go to voice agent
        setTimeout(() => {
          setMessages(prev => [...prev, { 
            type: 'bot', 
            text: "Thank you for sharing. Let's talk more about what's on your mind."
          }])
          setSupportState(prev => ({
            ...prev,
            currentStep: 'voice',
            emotionalResponses: [...prev.emotionalResponses, ...newResponses]
          }))
        }, 1000)
      }
    }
  }

  const handleActionButton = (action: string) => {
    const newUsedActions = [...supportState.usedActions, action]
    setSupportState(prev => ({
      ...prev,
      currentStep: 'action',
      usedActions: newUsedActions
    }))

    switch (action) {
      case 'calm':
        setShowBreathing(true)
        break
      case 'write':
        setShowJournal(true)
        break
      case 'quiet':
        setShowQuiet(true)
        break
    }
  }

  const handleFeelBetter = (feelsBetter: boolean) => {
    if (feelsBetter) {
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: 'I\'m so glad you\'re feeling a bit better. You did great reaching out.' 
      }])
      setTimeout(() => {
        // Navigate back to the support options page
        navigate('/support', { replace: true })
      }, 2000)
    } else {
      const availableActions = ['calm', 'write', 'quiet'].filter(
        action => !supportState.usedActions.includes(action)
      )
      
      if (availableActions.length > 0) {
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: 'That\'s okay. Let\'s try something else that might help.' 
        }])
        setSupportState(prev => ({
          ...prev,
          currentStep: 'recognized'
        }))
      } else {
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: 'You\'ve been so brave trying different things. Sometimes healing takes time, and that\'s okay.' 
        }])
        setTimeout(() => {
          // Navigate back to the support options page
          navigate('/support', { replace: true })
        }, 3000)
      }
    }
    
    setShowBreathing(false)
    setShowJournal(false)
    setShowQuiet(false)
  }

  // Generate comprehensive context summary for voice agent
  const generateContextSummary = (): string => {
    const emotion = supportState.recognizedEmotion || 'uncertain'
    const moodContext = supportState.moodContext
    
    let context = `User Context for Support Session:

${moodContext ? `Initial Mood Check-in: The user selected "${moodContext.mood}" when asked how they were feeling.` : ''}

Current Emotional State: The user expressed feeling ${emotion}.

Check-in Responses:`
    
    // Add each question and response pair
    checkInQuestions.forEach((question, index) => {
      if (userResponses[index]) {
        context += `
Q${index + 1}: ${question}
A${index + 1}: ${userResponses[index]}`
      }
    })

    context += `

Summary: The user has chosen to talk directly with you. They may be experiencing ${emotion} feelings or emotions. Please provide gentle, empathetic support that acknowledges their emotional state. Be warm, understanding, and offer practical comfort while validating their feelings.

${moodContext ? `Their initial mood selection was: ${moodContext.mood}` : 'They haven\'t shared specific details yet, so create a safe space for them to open up.'}

Please speak to them as a caring, non-judgmental companion who is here to listen and support them.`

    return context
  }

  const BreathingExercise = () => (
    <div className="bg-sage-50 dark:bg-sage-800/60 p-8 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 my-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-sage-100/60 dark:bg-sage-700/60 rounded-full flex items-center justify-center">
          <div className="w-10 h-10 bg-sage-200/50 dark:bg-sage-600/50 rounded-full"></div>
        </div>
        <p className="text-sage-600 dark:text-sage-300 font-light mb-4 text-sm">
          Let's breathe together. Go at your own pace.
        </p>
        <p className="text-sage-500 dark:text-sage-400 text-xs font-light mb-8">
          In through your nose, out through your mouth
        </p>
      </div>
      <div className="text-center">
        <button
          onClick={() => handleFeelBetter(true)}
          className="text-sage-500 dark:text-sage-400 text-sm underline hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
        >
          Take your time
        </button>
      </div>
    </div>
  )

  const JournalPrompt = () => (
    <div className="bg-cream-50 dark:bg-cream-900/30 p-8 rounded-3xl border border-cream-100/50 dark:border-cream-700/50 my-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 mx-auto mb-4 bg-cream-100/60 dark:bg-cream-800/60 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-cream-400 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </div>
        <p className="text-cream-600 dark:text-cream-300 font-light mb-4 text-sm">
          Use this space to write or release. No one sees it. No rules.
        </p>
      </div>
      <textarea
        placeholder="What's one small thing that felt okay today?..."
        className="w-full p-4 rounded-2xl border border-cream-200/50 dark:border-cream-700/50 focus:border-cream-300 dark:focus:border-cream-600 focus:ring-2 focus:ring-cream-100 dark:focus:ring-cream-700 focus:outline-none font-light resize-none text-sm bg-white/50 dark:bg-cream-900/50 dark:text-cream-300"
        rows={3}
      />
      <div className="mt-6 text-center">
        <button
          onClick={() => handleFeelBetter(true)}
          className="text-cream-500 dark:text-cream-400 text-sm underline hover:text-cream-600 dark:hover:text-cream-300 transition-colors"
        >
          You can come back to this anytime
        </button>
      </div>
    </div>
  )

  const QuietSpace = () => (
    <div className="bg-sage-50 dark:bg-sage-800/60 p-10 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 my-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-8 bg-sage-100/60 dark:bg-sage-700/60 rounded-full flex items-center justify-center">
          <div className="w-10 h-10 bg-sage-200/30 dark:bg-sage-600/30 rounded-full"></div>
        </div>
        <p className="text-sage-500 dark:text-sage-400 font-light mb-8 text-sm">
          This is your quiet space. Take all the time you need.
        </p>
        <div className="text-sage-400 dark:text-sage-500 text-xs font-light mb-10">
          You are safe here
        </div>
        <button
          onClick={() => handleFeelBetter(true)}
          className="text-sage-500 dark:text-sage-400 text-sm underline hover:text-sage-600 dark:hover:text-sage-300 transition-colors"
        >
          There's no right or wrong way to use this
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen flex flex-col bg-neutral-25 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-neutral-50/80 dark:bg-sage-800/80 border-b border-neutral-100/50 dark:border-sage-700/50">
        {/* Exit Button */}
        <button
          onClick={handleExit}
          className="p-3 sm:p-4 rounded-2xl hover:bg-neutral-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-neutral-100 dark:focus:ring-sage-700"
          aria-label="Exit Dopa Listens"
          title="Return to support options"
        >
          <svg className="w-5 h-5 text-neutral-500 dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        {/* Center Logo */}
        <div className="flex items-center">
          <DopaLogo size="md" withText={true} className="justify-center" />
        </div>
        
        {/* Empty div to balance the header */}
        <div className="w-11 sm:w-12"></div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 sm:p-6 lg:p-8 space-y-6 overflow-y-auto">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs p-4 sm:p-6 rounded-3xl ${
                message.type === 'user'
                  ? message.isButton 
                    ? 'bg-sage-100/60 dark:bg-sage-800/60 text-sage-600 dark:text-sage-300 border border-sage-200/50 dark:border-sage-700/50'
                    : 'bg-cream-100/60 dark:bg-cream-900/30 text-cream-600 dark:text-cream-300 border border-cream-200/50 dark:border-cream-700/50'
                  : 'bg-white/80 dark:bg-sage-800/80 text-neutral-600 dark:text-neutral-300 border border-neutral-100/50 dark:border-neutral-700/50'
              }`}
            >
              <p className="font-light leading-relaxed text-sm">{message.text}</p>
            </div>
          </div>
        ))}

        {/* Initial emotion buttons - only show if no mood context */}
        {supportState.currentStep === 'initial' && !supportState.moodContext && (
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            {['Overwhelmed', 'Panicking', 'Anxious', 'Tired', 'Sad', 'Confused'].map((emotion) => (
              <button
                key={emotion}
                onClick={() => handleEmotionButton(emotion)}
                className="px-6 py-3 bg-sage-50 dark:bg-sage-800 hover:bg-sage-100/80 dark:hover:bg-sage-700/80 
                           text-sage-600 dark:text-sage-300 rounded-2xl text-sm font-light border border-sage-100/50 dark:border-sage-700/50 hover:border-sage-200/60 dark:hover:border-sage-600/60
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100 dark:focus:ring-sage-700"
              >
                {emotion}
              </button>
            ))}
          </div>
        )}

        {/* Voice Agent - Show immediately */}
        {supportState.currentStep === 'voice' && (
          <DopaVoiceAgent contextSummary={generateContextSummary()} />
        )}

        {/* Action components */}
        {showBreathing && <BreathingExercise />}
        {showJournal && <JournalPrompt />}
        {showQuiet && <QuietSpace />}

        {/* Follow-up buttons */}
        {supportState.currentStep === 'action' && (showBreathing || showJournal || showQuiet) && (
          <div className="mt-8 text-center">
            <p className="text-neutral-500 dark:text-neutral-400 font-light text-sm mb-6">
              Do you feel a little better?
            </p>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center">
              <button
                onClick={() => handleFeelBetter(true)}
                className="px-6 sm:px-8 py-3 bg-sage-50 dark:bg-sage-800 hover:bg-sage-100/80 dark:hover:bg-sage-700/80 
                           text-sage-600 dark:text-sage-300 rounded-2xl text-sm font-light border border-sage-100/50 dark:border-sage-700/50 hover:border-sage-200/60 dark:hover:border-sage-600/60
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100 dark:focus:ring-sage-700
                           flex items-center justify-center space-x-2"
              >
                <span>😊</span>
                <span>Yes, I'm OK now</span>
              </button>
              <button
                onClick={() => handleFeelBetter(false)}
                className="px-6 sm:px-8 py-3 bg-cream-50 dark:bg-cream-900/30 hover:bg-cream-100/80 dark:hover:bg-cream-800/40 
                           text-cream-600 dark:text-cream-300 rounded-2xl text-sm font-light border border-cream-100/50 dark:border-cream-700/50 hover:border-cream-200/60 dark:hover:border-cream-600/60
                           transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100 dark:focus:ring-cream-700
                           flex items-center justify-center space-x-2"
              >
                <span>😔</span>
                <span>Not yet</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Input - only show if no mood context or in recognized step */}
      {((supportState.currentStep === 'initial' && !supportState.moodContext) || supportState.currentStep === 'recognized') && (
        <div className="p-4 sm:p-6 lg:p-8 bg-neutral-50/80 dark:bg-sage-800/80 border-t border-neutral-100/50 dark:border-sage-700/50">
          <div className="flex space-x-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={
                supportState.currentStep === 'initial' 
                  ? "Or type how you're feeling..." 
                  : "Share your thoughts..."
              }
              className="flex-1 p-4 rounded-2xl border border-neutral-200/50 dark:border-sage-700/50 focus:border-neutral-300 dark:focus:border-sage-600 focus:ring-2 focus:ring-neutral-100 dark:focus:ring-sage-700 focus:outline-none font-light text-sm bg-white/50 dark:bg-sage-900/50 dark:text-neutral-300"
            />
            <button
              onClick={handleSendMessage}
              className="p-4 bg-neutral-100/60 hover:bg-neutral-200/60 
                         dark:bg-neutral-800/60 dark:hover:bg-neutral-700/60
                         text-neutral-600 dark:text-neutral-300 rounded-2xl 
                         border border-neutral-200/50 dark:border-neutral-700/50 
                         hover:border-neutral-300/60 dark:hover:border-neutral-600/60
                         transition-colors duration-300 focus:outline-none focus:ring-4 
                         focus:ring-neutral-100 dark:focus:ring-neutral-700/50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SupportBot